/* pi-cart-drawer.js ====================================================
   Aurora-style cart drawer.
   - Custom element <pi-cart-drawer> handles open/close, focus trap, scroll
     lock, section re-render, free-shipping bar, dispatch timer, shipping
     rate estimator, and recommendations carousel quick-add.
   - <pi-cart-drawer-items> extends Dawn's CartItems so we get the line
     item update + remove logic for free, but with our own re-render
     section targets.
   ====================================================================== */

(function () {
  // Cart drawer open/close + global plumbing.
  if (!window.customElements.get('pi-cart-drawer')) {
    class PiCartDrawer extends HTMLElement {
      constructor() {
        super();
        this.untrap = null;
        this.trigger = null;
      }

      connectedCallback() {
        this.hookCartIcon();
        this.bindClose();
        this.bindKeys();
        this.bindRemoveButtons();
        this.initShippingBar();
        this.initDispatchTimer();
        this.initPopupTabs();
        this.initEstimator();
        this.initDynamicCheckout();
        this.subscribeCartUpdates();
        this.observeReRenders();
      }

      disconnectedCallback() {
        if (this.dataset.open === 'true') this.close();
      }

      /* Dawn CartRemoveButton only looks for cart-drawer-items, not
         pi-cart-drawer-items — route remove clicks here in capture phase. */
      bindRemoveButtons() {
        if (this._removeCaptureBound) return;
        this._removeCaptureBound = true;
        this.addEventListener(
          'click',
          (event) => {
            const removeEl = event.target.closest('cart-remove-button');
            if (!removeEl || !this.contains(removeEl)) return;
            const items = removeEl.closest('pi-cart-drawer-items');
            if (!items) return;
            event.preventDefault();
            event.stopPropagation();
            items.updateQuantity(removeEl.dataset.index, 0, event);
          },
          true
        );
      }

      observeReRenders() {
        // Dawn's CartItems re-renders innerHTML in-place. When it does, our
        // event bindings (close button, dispatch timer interval, quick-add
        // listeners) need to be re-attached.
        if (this._observer) this._observer.disconnect();
        this._observer = new MutationObserver(() => {
          // Debounce; many mutations fire in rapid succession on re-render.
          if (this._reinitTimer) clearTimeout(this._reinitTimer);
          this._reinitTimer = setTimeout(() => {
            this.bindClose();
            this.initShippingBar();
            this.initDispatchTimer();
            this.initPopupTabs();
            this.initEstimator();
            this.initDynamicCheckout();
            this.dataset.empty = String(!!this.querySelector('.pi-cart-empty'));
          }, 16);
        });
        this._observer.observe(this, { childList: true, subtree: false });
      }

      bindKeys() {
        document.addEventListener('keydown', (e) => {
          if (e.key !== 'Escape' || this.dataset.open !== 'true') return;
          // Close any open popup first; if none, close the drawer itself.
          const openPopup = this.querySelector('.pi-cart-popup.is-open');
          if (openPopup) {
            this.closePopup(openPopup.id);
          } else {
            this.close();
          }
        });
      }

      hookCartIcon() {
        const icon = document.querySelector('#cart-icon-bubble');
        if (!icon) return;
        icon.setAttribute('role', 'button');
        icon.setAttribute('aria-haspopup', 'dialog');
        icon.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(icon);
        });
        icon.addEventListener('keydown', (e) => {
          if (e.code === 'Space' || e.code === 'Enter') {
            e.preventDefault();
            this.open(icon);
          }
        });
      }

      bindClose() {
        this.querySelectorAll('[data-pi-cart-close]').forEach((btn) => {
          if (btn.dataset.piCartCloseBound) return;
          btn.dataset.piCartCloseBound = '1';
          btn.addEventListener('click', () => this.close());
        });
      }

      /* Dawn product-form.js compatibility */
      getSectionsToRender() {
        return [
          { id: 'pi-cart-drawer' },
          { id: 'pi-cart-icon-bubble' },
        ];
      }

      renderContents(parsedState) {
        if (parsedState?.sections) {
          PiCartDrawer.applySections(parsedState.sections);
        } else {
          this.refreshFromServer(false);
        }
        this.dataset.empty = 'false';
        setTimeout(() => this.open(this.trigger || undefined), 0);
      }

      setActiveElement(element) {
        this.trigger = element || null;
      }

      open(triggeredBy) {
        this.trigger = triggeredBy || this.trigger || null;
        this.dataset.open = 'true';
        this.setAttribute('aria-hidden', 'false');
        if (window.PiUtils) {
          window.PiUtils.lockScroll(this);
          if (this.untrap) this.untrap();
          this.untrap = window.PiUtils.trapFocus(
            this,
            this.querySelector('.pi-cart__close, .pi-cart__checkout')
          );
        }
      }

      close() {
        if (this.dataset.open !== 'true') return;
        this.dataset.open = 'false';
        this.setAttribute('aria-hidden', 'true');
        if (this.untrap) {
          this.untrap();
          this.untrap = null;
        }
        if (window.PiUtils) window.PiUtils.unlockScroll(this);
        if (this.trigger && typeof this.trigger.focus === 'function') {
          this.trigger.focus({ preventScroll: true });
        }
      }

      /* -------------------- Section re-render ----------------------
         Each entry pairs the SECTION id (used to fetch + look up in
         the response JSON) with the DOM id+selector pair that we
         actually want to replace in-place. */
      static getSectionsToRender() {
        const drawer = document.getElementById('pi-cart-drawer-root');
        const drawerSection = drawer?.dataset.sectionId || 'pi-cart-drawer';

        return [
          {
            id: 'pi-cart-drawer-root',
            section: drawerSection,
            selector: 'pi-cart-drawer',
          },
          {
            id: 'cart-icon-bubble',
            section: 'pi-cart-icon-bubble',
            selector: 'pi-cart-icon-bubble',
          },
        ];
      }

      static getSectionHtml(sections, sectionKey) {
        if (!sections || !sectionKey) return null;
        if (sections[sectionKey]) return sections[sectionKey];

        const match = Object.keys(sections).find(
          (key) =>
            key === sectionKey ||
            key.endsWith(`__${sectionKey}`) ||
            key.includes(sectionKey)
        );

        return match ? sections[match] : null;
      }

      static getSectionsQuery() {
        return PiCartDrawer.getSectionsToRender()
          .map((s) => s.section)
          .join(',');
      }

      subscribeCartUpdates() {
        try {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
              // product-form already re-renders + opens via renderContents().
              if (event?.source === 'product-form') return;
              // External add-to-cart — refresh and open drawer to confirm.
              if (event && event.source && event.source !== 'pi-cart-drawer-items') {
                this.refreshFromServer(true);
              }
            });
          }
        } catch (e) {
          /* ignore */
        }
      }

      async refreshFromServer(openAfter) {
        try {
          const url = `${window.location.pathname}?sections=${PiCartDrawer.getSectionsQuery()}`;
          const res = await fetch(url);
          const json = await res.json();
          PiCartDrawer.applySections(json);
          if (openAfter) this.open();
        } catch (e) {
          console.error('pi-cart-drawer: refresh failed', e);
        }
      }

      static applySections(json) {
        if (!json || typeof json !== 'object') return false;

        PiCartDrawer.getSectionsToRender().forEach((section) => {
          const html = PiCartDrawer.getSectionHtml(json, section.section);
          if (!html) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');

          if (section.section === 'pi-cart-drawer') {
            const drawer = document.getElementById('pi-cart-drawer-root');
            const node = doc.querySelector('pi-cart-drawer');
            if (!drawer || !node) return;
            drawer.innerHTML = node.innerHTML;
            drawer.dataset.empty = node.dataset.empty || 'false';
            return;
          }

          if (section.section === 'pi-cart-icon-bubble') {
            const target = document.getElementById('cart-icon-bubble');
            const node =
              doc.getElementById('shopify-section-pi-cart-icon-bubble') ||
              doc.querySelector('.shopify-section');
            if (!target || !node) return;
            target.innerHTML = node.innerHTML;
          }
        });

        // Re-init bits that need binding after content swap.
        const drawer = document.querySelector('pi-cart-drawer');
        if (drawer && typeof drawer.bindClose === 'function') {
          drawer.bindClose();
          drawer.initShippingBar();
          drawer.initDispatchTimer();
          drawer.initPopupTabs();
          drawer.initEstimator();
          drawer.initDynamicCheckout();
        }

        return true;
      }

      static formatCartErrors(errors) {
        if (!errors) return '';
        if (typeof errors === 'string') return errors;
        if (Array.isArray(errors)) return errors.join(', ');
        if (typeof errors === 'object') {
          return Object.values(errors)
            .flat()
            .filter(Boolean)
            .join(', ');
        }
        return String(errors);
      }

      /* -------------------- Popup tab bar -------------------------- */
      initPopupTabs() {
        // Bind open buttons (data-pi-popup-open="<id>")
        this.querySelectorAll('[data-pi-popup-open]').forEach((btn) => {
          if (btn.dataset.piPopupBound) return;
          btn.dataset.piPopupBound = '1';
          btn.addEventListener('click', () => {
            this.openPopup(btn.getAttribute('data-pi-popup-open'));
          });
        });

        // Bind close buttons (data-pi-popup-close="<id>")
        this.querySelectorAll('[data-pi-popup-close]').forEach((btn) => {
          if (btn.dataset.piPopupCloseBound) return;
          btn.dataset.piPopupCloseBound = '1';
          btn.addEventListener('click', () => {
            this.closePopup(btn.getAttribute('data-pi-popup-close'));
          });
        });
      }

      openPopup(id) {
        const popup = this.querySelector('#' + id);
        if (!popup) return;
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');

        // Populate province selector when the shipping popup is opened.
        if (id === 'pi-cart-shipping-popup') {
          this.initProvinceSelector(popup);
        }
      }

      closePopup(id) {
        const popup = this.querySelector('#' + id);
        if (!popup) return;
        popup.classList.remove('is-open');
        popup.setAttribute('aria-hidden', 'true');
      }

      /* -------------------- Province selector ----------------------
         Shopify's country_option_tags renders each <option> with a
         data-provinces attribute containing a JSON array of
         [["Province Name", "code"], ...] pairs. We use that to
         populate the province <select> whenever the country changes. */
      initProvinceSelector(popup) {
        const countrySelect = popup.querySelector('#PiEstCountry');
        const provinceSelect = popup.querySelector('#PiEstProvince');
        const provinceField = provinceSelect && provinceSelect.closest('.pi-cart-popup__field');
        if (!countrySelect || !provinceSelect) return;
        if (countrySelect.dataset.piProvinceBound) return;
        countrySelect.dataset.piProvinceBound = '1';

        const updateProvinces = () => {
          const selected = countrySelect.options[countrySelect.selectedIndex];
          const raw = selected ? selected.getAttribute('data-provinces') : null;
          let provinces = [];
          try {
            provinces = raw ? JSON.parse(raw) : [];
          } catch (e) { /* ignore */ }

          // Show/hide province field based on availability.
          if (provinceField) {
            provinceField.style.display = provinces.length ? '' : 'none';
          }

          provinceSelect.innerHTML = '';
          provinces.forEach(([name]) => {
            const opt = document.createElement('option');
            opt.value = name;
            opt.textContent = name;
            provinceSelect.appendChild(opt);
          });
        };

        countrySelect.addEventListener('change', updateProvinces);
        // Run once on open to reflect default country.
        updateProvinces();
      }

      /* -------------------- Free-shipping bar ---------------------- */
      static getShippingGradientColor(position) {
        const stops = [
          { at: 0, color: [14, 165, 233] },
          { at: 0.35, color: [79, 70, 229] },
          { at: 0.65, color: [236, 72, 153] },
          { at: 1, color: [249, 115, 22] },
        ];
        const t = Math.max(0, Math.min(1, position / 100));
        let start = stops[0];
        let end = stops[stops.length - 1];

        for (let i = 0; i < stops.length - 1; i++) {
          if (t >= stops[i].at && t <= stops[i + 1].at) {
            start = stops[i];
            end = stops[i + 1];
            break;
          }
        }

        const span = end.at - start.at || 1;
        const local = (t - start.at) / span;
        const rgb = start.color.map((channel, index) =>
          Math.round(channel + (end.color[index] - channel) * local)
        );

        return `rgb(${rgb.join(', ')})`;
      }

      initShippingBar() {
        const bar = this.querySelector('.pi-cart-shipping-bar');
        if (!bar) return;
        const threshold = parseFloat(bar.dataset.threshold || '0');
        const total = parseFloat(bar.dataset.total || '0');
        if (!threshold || threshold <= 0) return;

        const track = bar.querySelector('.pi-cart-shipping-bar__progress');
        if (!track) return;

        let segments = track.querySelector('.pi-cart-shipping-bar__segments');
        if (!segments) {
          segments = document.createElement('div');
          segments.className = 'pi-cart-shipping-bar__segments';
          track.innerHTML = '';
          track.appendChild(segments);
        }

        const unlocked = bar.dataset.unlocked === 'true';
        const pct = unlocked ? 100 : Math.min(100, (total / threshold) * 100);
        const dashCount = 48;

        segments.innerHTML = '';
        for (let i = 0; i < dashCount; i++) {
          const dash = document.createElement('span');
          dash.className = 'pi-cart-shipping-bar__dash';
          const dashPct = ((i + 0.5) / dashCount) * 100;

          if (dashPct <= pct) {
            dash.classList.add('is-filled');
            dash.style.backgroundColor = PiCartDrawer.getShippingGradientColor(dashPct);
          }

          segments.appendChild(dash);
        }
      }

      /* -------------------- Dispatch timer ------------------------- */
      initDispatchTimer() {
        const el = this.querySelector('.pi-cart-dispatch');
        if (!el) return;
        const cutoff = el.dataset.cutoff;
        const skipWeekend = el.dataset.weekend === 'true';
        const labelEl = el.querySelector('.pi-cart-dispatch__text');
        const cutoffLabel = el.dataset.cutoffLabel || cutoff;
        if (!labelEl || !cutoff) return;

        const compute = () => {
          const now = new Date();
          const day = now.getDay(); // 0 Sun .. 6 Sat
          const isWeekend = day === 0 || day === 6;
          const [hh, mm] = cutoff.split(':').map((n) => parseInt(n, 10));
          const target = new Date(now);
          target.setHours(hh || 0, mm || 0, 0, 0);
          const diff = target.getTime() - now.getTime();

          if (skipWeekend && isWeekend) {
            el.dataset.closed = 'true';
            labelEl.textContent = el.dataset.copyClosed || 'Same-day dispatch closed today';
            return;
          }
          if (diff <= 0) {
            el.dataset.closed = 'true';
            labelEl.textContent = el.dataset.copyClosed || 'Same-day dispatch closed for today';
            return;
          }
          el.dataset.closed = 'false';
          const totalMin = Math.floor(diff / 60000);
          const hours = Math.floor(totalMin / 60);
          const mins = totalMin % 60;
          const remaining =
            (hours ? hours + 'h ' : '') + (mins.toString().padStart(2, '0')) + 'm';
          const tpl = el.dataset.copy || 'Order in {{ remaining }} for same-day dispatch';
          labelEl.textContent = tpl
            .replace('{{ remaining }}', remaining)
            .replace('{{ time }}', cutoffLabel);
        };

        compute();
        if (this._dispatchInterval) clearInterval(this._dispatchInterval);
        this._dispatchInterval = setInterval(compute, 60 * 1000);
      }

      /* -------------------- Shipping estimator --------------------- */
      initEstimator() {
        const form = this.querySelector('.pi-cart-estimator__form');
        if (!form || form.dataset.bound) return;
        form.dataset.bound = '1';
        const country = form.querySelector('[name="shipping_address[country]"]');
        const province = form.querySelector('[name="shipping_address[province]"]');
        const zip = form.querySelector('[name="shipping_address[zip]"]');
        const out = form.querySelector('.pi-cart-estimator__results');
        const err = form.querySelector('.pi-cart-estimator__error');

        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (out) out.innerHTML = '';
          if (err) err.textContent = '';
          const params = new URLSearchParams();
          if (country && country.value) params.set('shipping_address[country]', country.value);
          if (province && province.value) params.set('shipping_address[province]', province.value);
          if (zip && zip.value) params.set('shipping_address[zip]', zip.value);

          try {
            const res = await fetch(`/cart/shipping_rates.json?${params.toString()}`);
            if (!res.ok) {
              const body = await res.json().catch(() => ({}));
              const messages = Array.isArray(body)
                ? body
                : Object.keys(body).map((k) => `${k}: ${body[k]}`);
              if (err) err.textContent = messages.join(', ') || 'Could not retrieve rates.';
              return;
            }
            const data = await res.json();
            const rates = (data && data.shipping_rates) || [];
            if (rates.length === 0) {
              if (err) err.textContent = 'No rates available for this address.';
              return;
            }
            if (out) {
              out.innerHTML = rates
                .map((r) => `<li><span>${r.name}</span><span>${r.price}</span></li>`)
                .join('');
            }
          } catch (e2) {
            if (err) err.textContent = 'Could not retrieve rates.';
          }
        });
      }

      /* -------------------- Dynamic checkout ----------------------- */
      initDynamicCheckout() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      }

      /* -------------------- Recommendation quick-add --------------- */
      initRecommendationsQuickAdd() {
        this.querySelectorAll('.pi-reco-card__quick-add').forEach((btn) => {
          if (btn.dataset.bound) return;
          btn.dataset.bound = '1';
          btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const id = btn.getAttribute('data-variant-id');
            if (!id) return;
            const body = new FormData();
            body.append('id', id);
            body.append('quantity', '1');
            const sections = PiCartDrawer.getSectionsQuery();
            body.append('sections', sections);
            body.append('sections_url', window.location.pathname);
            try {
              btn.disabled = true;
              const res = await fetch(window.routes.cart_add_url, {
                method: 'POST',
                headers: { Accept: 'application/javascript' },
                body,
              });
              const json = await res.json();
              if (json && json.sections) {
                PiCartDrawer.applySections(json.sections);
              } else {
                await this.refreshFromServer(false);
              }
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'pi-cart-drawer-quick-add',
                  cartData: json,
                });
              }
            } catch (e2) {
              console.error('pi-cart-drawer: quick-add failed', e2);
            } finally {
              btn.disabled = false;
            }
          });
        });
      }
    }

    window.customElements.define('pi-cart-drawer', PiCartDrawer);
  }

  /* ====================================================================
     <pi-cart-drawer-items> — extends CartItems for line-item updates.
     ==================================================================== */
  function defineCartItems() {
    if (window.customElements.get('pi-cart-drawer-items')) return;
    if (typeof CartItems === 'undefined') {
      // Wait for cart.js (Dawn) which is loaded with defer before this.
      return setTimeout(defineCartItems, 50);
    }

    class PiCartDrawerItems extends CartItems {
      constructor() {
        super();
        this.lineItemStatusElement = document.getElementById('PiCartLineItemStatus');
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        try {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
              if (event.source === 'pi-cart-drawer-items' || event.source === 'product-form') return;
              this.onCartUpdate();
            });
          }
        } catch (e) {
          /* ignore */
        }
      }

      getSectionsToRender() {
        return [
          {
            id: 'pi-cart-drawer-root',
            section: 'pi-cart-drawer',
            selector: 'pi-cart-drawer',
          },
          {
            id: 'cart-icon-bubble',
            section: 'pi-cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }

      getSectionInnerHTML(html, selector = 'pi-cart-drawer') {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector(selector);
        return node ? node.innerHTML : '';
      }

      onCartUpdate() {
        const drawer = document.querySelector('pi-cart-drawer');
        if (drawer && typeof drawer.refreshFromServer === 'function') {
          drawer.refreshFromServer(false);
        }
      }

      updateQuantity(line, quantity, event, name, variantId) {
        this.enableLoading(line);

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((data) => {
                throw new Error(
                  PiCartDrawer.formatCartErrors(data?.errors || data?.description) ||
                    'Cart update failed'
                );
              });
            }
            return response.json();
          })
          .then((parsedState) => {
            const errorMessage = PiCartDrawer.formatCartErrors(parsedState.errors);
            if (errorMessage) {
              const input = document.querySelector(`#Quantity-${line}`);
              if (input) input.value = input.getAttribute('value') || input.value;
              this.updateLiveRegions(line, errorMessage);
              return;
            }

            let rendered = false;
            if (parsedState.sections) {
              rendered = PiCartDrawer.applySections(parsedState.sections);
            }

            if (!rendered) {
              const drawer = document.querySelector('pi-cart-drawer');
              if (drawer && typeof drawer.refreshFromServer === 'function') {
                return drawer.refreshFromServer(false);
              }
            }

            const drawer = document.querySelector('pi-cart-drawer');
            if (drawer) {
              drawer.dataset.empty = String(parsedState.item_count === 0);
            }

            this.updateLiveRegions(line, '');

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'pi-cart-drawer-items',
                cartData: parsedState,
                variantId,
              });
            }
          })
          .catch((err) => {
            console.error('pi-cart-drawer: updateQuantity failed', err);
            const drawer = document.querySelector('pi-cart-drawer');
            if (drawer && typeof drawer.refreshFromServer === 'function') {
              drawer.refreshFromServer(false);
              return;
            }
            this.updateLiveRegions(
              line,
              err?.message || window.cartStrings?.error || 'Could not update cart.'
            );
          })
          .finally(() => {
            const row = document.querySelector(`#PiCartItem-${line}`);
            if (row) row.classList.remove('is-updating');
            const status = document.getElementById('PiCartLineItemStatus');
            if (status) status.setAttribute('aria-hidden', 'true');
          });
      }

      updateLiveRegions(line, message) {
        const lineItemError = document.getElementById(`PiCartLineError-${line}`);
        if (lineItemError) {
          const textEl = lineItemError.querySelector('.pi-cart-item__error-text');
          if (textEl) textEl.textContent = message;
          if (message) {
            lineItemError.dataset.active = 'true';
          } else {
            lineItemError.removeAttribute('data-active');
          }
        }

        if (!this.lineItemStatusElement) return;
        this.lineItemStatusElement.setAttribute('aria-hidden', message ? 'false' : 'true');
        if (message) {
          setTimeout(() => {
            this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
          }, 1000);
        }
      }

      enableLoading(line) {
        const row = this.querySelector(`#PiCartItem-${line}`);
        if (row) row.classList.add('is-updating');
        if (this.lineItemStatusElement) {
          this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
        }
      }

      disableLoading(line) {
        const row = this.querySelector(`#PiCartItem-${line}`);
        if (row) row.classList.remove('is-updating');
        if (this.lineItemStatusElement) {
          this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
        }
      }
    }

    window.customElements.define('pi-cart-drawer-items', PiCartDrawerItems);
  }

  defineCartItems();
})();
